import React, { useState, useRef, useEffect } from "react";
import BaseForm from "../../app/components/BaseForm";
import Input from "../../app/components/Input";
import Select from "../../app/components/Select";
import Button from "../../app/components/Button";
import Accordion from "../../app/components/Accordion";
import { FiMail, FiBold, FiItalic, FiUnderline } from "react-icons/fi";
import { FaListOl, FaListUl } from "react-icons/fa";

const TagButton = ({ label, onClick, icon }) => (
  <Button
    type="button"
    variant="secondary"
    onClick={onClick}
    className="text-sm flex items-center gap-2 justify-start"
  >
    {icon}
    <span>{label}</span>
  </Button>
);

const ToolbarButton = ({ icon, onClick, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className="p-2 rounded hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
  >
    {icon}
  </button>
);

const EmailSequenceEdit = ({ isOpen, onClose, templateData, onSave }) => {
  const editorRef = useRef(null);
  const [hasContent, setHasContent] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [formData, setFormData] = useState({
    nome: "",
    destinatario: "",
    assunto: "",
    corpoEmail: "",
  });

  const [expandedSections, setExpandedSections] = useState([]);

  useEffect(() => {
  if (templateData) {
    setFormData({
      id: templateData.id,
      nome: templateData.nome || "",
      destinatario: templateData.destinatario || "",
      assunto: templateData.assunto || "",
      corpoEmail: templateData.corpoEmail || "",
    });
  } else {
    setFormData({
      nome: "",
      destinatario: "",
      assunto: "",
      corpoEmail: "",
    });
  }
}, [templateData]);


  useEffect(() => {
    if (!editorRef.current) return;
    if (editorRef.current.innerHTML !== formData.corpoEmail) {
      editorRef.current.innerHTML = formData.corpoEmail;
    }
  }, [formData.corpoEmail]);

  useEffect(() => {
    if (editorRef.current) {
      const text = editorRef.current.innerText;
      setHasContent(text.trim().length > 0);
    }
  }, [formData.corpoEmail]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditorInput = () => {
    if (!editorRef.current) return;

    const html = editorRef.current.innerHTML;
    const text = editorRef.current.innerText;

    setFormData((prev) => ({
      ...prev,
      corpoEmail: html,
    }));

    setHasContent(text.trim().length > 0);
  };

  const handleInsertTag = (tagLabel) => {
    const tag = `{{${tagLabel}}}`;

    if (!editorRef.current) return;

    editorRef.current.focus();

    const sel = window.getSelection();
    if (!sel || !sel.getRangeAt || sel.rangeCount === 0) {
      editorRef.current.innerHTML += tag;
      handleEditorInput();
      return;
    }

    const range = sel.getRangeAt(0);
    range.deleteContents();

    const textNode = document.createTextNode(tag);
    range.insertNode(textNode);

    range.setStartAfter(textNode);
    range.collapse(true);

    sel.removeAllRanges();
    sel.addRange(range);

    handleEditorInput();
  };

  const execCommand = (command) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(command, false, null);
    handleEditorInput();
  };

  const toggleSection = (id) => {
    setExpandedSections((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const isExpanded = (id) => expandedSections.includes(id);

  const recipientOptions = [
    { value: "email", label: "Email" },
    { value: "indicadoPor", label: "Indicado por" },
  ];

  const accordionSections = [
    {
      id: "formulario-inicial",
      title: "Formulário inicial",
      expanded: isExpanded("formulario-inicial"),
      items: [
        <TagButton
          icon={<FiMail />}
          key="nome-cliente"
          label="Nome do cliente"
          onClick={() => handleInsertTag("Nome do cliente")}
        />,
        <TagButton
          icon={<FiMail />}
          key="email-cliente"
          label="Email do cliente"
          onClick={() => handleInsertTag("Email do cliente")}
        />,
        <TagButton
          icon={<FiMail />}
          key="telefone"
          label="Telefone"
          onClick={() => handleInsertTag("Telefone")}
        />,
        <TagButton
          icon={<FiMail />}
          key="cnpj"
          label="CNPJ"
          onClick={() => handleInsertTag("CNPJ")}
        />,
        <TagButton
          icon={<FiMail />}
          key="razao-social"
          label="Razão social"
          onClick={() => handleInsertTag("Razão social")}
        />,
        <TagButton
          icon={<FiMail />}
          key="nome-fantasia"
          label="Nome fantasia"
          onClick={() => handleInsertTag("Nome fantasia")}
        />,
        <TagButton
          icon={<FiMail />}
          key="regime"
          label="Regime"
          onClick={() => handleInsertTag("Regime")}
        />,
        <TagButton
          icon={<FiMail />}
          key="tipo-de-entreda"
          label="Tipo de entrada"
          onClick={() => handleInsertTag("Tipo de entrada")}
        />,
        <TagButton
          icon={<FiMail />}
          key="valor-honorário-abertura"
          label="Valor (R$) honorário de abertura"
          onClick={() => handleInsertTag("Valor (R$) honorário de abertura")}
        />,
        <TagButton
          icon={<FiMail />}
          key="valor-honorário-mensal"
          label="Valor (R$) honorário de mensal"
          onClick={() => handleInsertTag("Valor (R$) honorário de mensal")}
        />,
        <TagButton
          icon={<FiMail />}
          key="segmento"
          label="Segmento"
          onClick={() => handleInsertTag("Segmento")}
        />,
        <TagButton
          icon={<FiMail />}
          key="funcionarios?"
          label="A empresa possui quantos funcionários?"
          onClick={() =>
            handleInsertTag("A empresa possui quantos funcionários")
          }
        />,
        <TagButton
          icon={<FiMail />}
          key="movimento?"
          label="Empresa tem movimento?"
          onClick={() => handleInsertTag("Empresa tem movimento?")}
        />,
      ],
    },
    {
      id: "lead",
      title: "Lead quente",
      expanded: isExpanded("lead"),
      items: [
        <TagButton
          icon={<FiMail />}
          key="fechamento"
          label="Data do fechamento esperado"
          onClick={() => handleInsertTag("Data do fechamento esperado")}
        />,
        <TagButton
          icon={<FiMail />}
          key="temperatura-do-negocio"
          label="Temperatura do Negócio"
          onClick={() => handleInsertTag("Temperatura do Negócio")}
        />,
        <TagButton
          icon={<FiMail />}
          key="observacoes"
          label="Observções:"
          onClick={() => handleInsertTag("Observções:")}
        />,
      ],
    },
    {
      id: "negociacao",
      title: "Negociação",
      expanded: isExpanded("negociacao"),
      items: [
        <TagButton
          icon={<FiMail />}
          key="fechamento"
          label="Data do fechamento esperado"
          onClick={() => handleInsertTag("Data do fechamento esperado")}
        />,
        <TagButton
          icon={<FiMail />}
          key="temperatura-do-negocio"
          label="Temperatura do Negócio"
          onClick={() => handleInsertTag("Temperatura do Negócio")}
        />,
        <TagButton
          icon={<FiMail />}
          key="observacoes"
          label="Observções:"
          onClick={() => handleInsertTag("Observções:")}
        />,
      ],
    },
    {
      id: "abertura-vendida",
      title: "Abertura vendida",
      expanded: isExpanded("abertura-vendida"),
      items: [
        <TagButton
          icon={<FiMail />}
          key="fechamento"
          label="Data do fechamento esperado"
          onClick={() => handleInsertTag("Data do fechamento esperado")}
        />,
        <TagButton
          icon={<FiMail />}
          key="temperatura-do-negocio"
          label="Temperatura do Negócio"
          onClick={() => handleInsertTag("Temperatura do Negócio")}
        />,
        <TagButton
          icon={<FiMail />}
          key="observacoes"
          label="Observções:"
          onClick={() => handleInsertTag("Observções:")}
        />,
      ],
    },
    {
      id: "ganho",
      title: "Ganho (falta contrato)",
      expanded: isExpanded("ganho"),
      items: [
        <TagButton
          icon={<FiMail />}
          key="fechamento"
          label="Data do fechamento esperado"
          onClick={() => handleInsertTag("Data do fechamento esperado")}
        />,
        <TagButton
          icon={<FiMail />}
          key="temperatura-do-negocio"
          label="Temperatura do Negócio"
          onClick={() => handleInsertTag("Temperatura do Negócio")}
        />,
        <TagButton
          icon={<FiMail />}
          key="observacoes"
          label="Observções:"
          onClick={() => handleInsertTag("Observções:")}
        />,
      ],
    },
    {
      id: "contrato-assinado",
      title: "Contrato assinado",
      expanded: isExpanded("contrato-assinado"),
      items: [
        <TagButton
          icon={<FiMail />}
          key="fechamento"
          label="Data do fechamento esperado"
          onClick={() => handleInsertTag("Data do fechamento esperado")}
        />,
        <TagButton
          icon={<FiMail />}
          key="temperatura-do-negocio"
          label="Temperatura do Negócio"
          onClick={() => handleInsertTag("Temperatura do Negócio")}
        />,
        <TagButton
          icon={<FiMail />}
          key="observacoes"
          label="Observções:"
          onClick={() => handleInsertTag("Observções:")}
        />,
      ],
    },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
    onClose();
  };

  return (
    <BaseForm
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={templateData ? "Editar Template de Email" : "Criar Template de Email"}
    >
      <div className="space-y-4">
        <Input
          type="text"
          label="Nome do template"
          name="nome"
          value={formData.nome}
          onChange={handleChange}
        />

        <Select
          label="Destinatário"
          name="destinatario"
          value={formData.destinatario}
          onChange={handleChange}
          options={recipientOptions}
        />

        <Input
          type="text"
          label="Assunto"
          name="assunto"
          value={formData.assunto}
          onChange={handleChange}
        />

        <div className="pb-8">
          <h2 className="mb-4 mt-8 font-semibold text-lg">Campo dinâmico</h2>
          <Accordion sections={accordionSections} onToggleSection={toggleSection} />
        </div>

        <div className="flex gap-2 mb-2">
          <ToolbarButton
            icon={<FiBold />}
            onClick={() => execCommand("bold")}
            title="Negrito"
          />
          <ToolbarButton
            icon={<FiItalic />}
            onClick={() => execCommand("italic")}
            title="Itálico"
          />
          <ToolbarButton
            icon={<FiUnderline />}
            onClick={() => execCommand("underline")}
            title="Sublinhado"
          />
          <ToolbarButton
            icon={<FaListOl />}
            onClick={() => execCommand("insertOrderedList")}
            title="Lista numerada"
          />
          <ToolbarButton
            icon={<FaListUl />}
            onClick={() => execCommand("insertUnorderedList")}
            title="Lista com marcadores"
          />
        </div>

        <div className="relative w-full mb-5 border border-gray-300 rounded-2xl p-3 min-h-[100px] focus-within:ring-1 focus-within:ring-purple-500/25 focus-within:border-purple-700 transition-all">
          <div
            contentEditable
            ref={editorRef}
            onInput={handleEditorInput}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            className="w-full min-h-[100px] outline-none"
            aria-label="Corpo do Email"
            spellCheck={false}
          />
          <label
            htmlFor="corpoEmail"
            className={`absolute left-4 px-1 text-sm text-gray-500 transition-all duration-200 ease-in-out pointer-events-none
              ${isFocused || hasContent ? "-top-2 bg-white" : "top-3"}
              ${isFocused ? "text-purple-700" : "text-gray-500"}
            `}
          >
            Corpo do Email
          </label>
        </div>
      </div>
    </BaseForm>
  );
};

export default EmailSequenceEdit;
